import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRight,
	CheckCircle2,
	Gauge,
	type LucideIcon,
	ShieldCheck,
} from "lucide-react";
import { PricingTable } from "@/components/pricing-table";
import {
	MAIN_CONTENT_ID,
	PublicFooter,
	PublicHeader,
} from "@/components/public-layout";
import { Reveal } from "@/components/reveal";
import { buildPricingJsonLd, JsonLd } from "@/lib/json-ld";
import { buildPageHead } from "@/lib/seo-meta";

export const Route = createFileRoute("/pricing")({
	component: PricingPage,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: "/pricing",
			title: "Pricing - AgentFramer",
			description:
				"Top up your AgentFramer balance and let AI agents generate images, videos, and audio through MCP. No subscriptions, no seat fees, no monthly minimum.",
			ogImage: "pricing",
		}),
});

function PricingPage() {
	const valueProps: Array<{
		icon: LucideIcon;
		title: string;
		body: string;
	}> = [
		{
			icon: ShieldCheck,
			title: "Managed providers",
			body: "Provider credentials, storage, and delivery are handled for you.",
		},
		{
			icon: Gauge,
			title: "Async execution",
			body: "Your agent gets structured job status and retrievable output URLs.",
		},
		{
			icon: CheckCircle2,
			title: "MCP out of the box",
			body: "CLI setup works with Claude, Cursor, Windsurf, Codex, and compatible clients.",
		},
	];

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<JsonLd data={buildPricingJsonLd()} />
			<PublicHeader />
			<main id={MAIN_CONTENT_ID} className="flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="flex flex-col gap-px bg-border">
						<div
							aria-hidden="true"
							className="h-14 rounded-md bg-background sm:h-16"
						/>

						<section className="homepage-hover-cell rounded-md bg-background px-6 py-12 text-center sm:px-10 sm:py-16">
							<Reveal>
								<p className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground/70">
									Balance-based media generation
								</p>
								<h1 className="mx-auto mt-3 max-w-3xl text-4xl font-semibold leading-tight sm:text-5xl">
									Top up once. Let your agent spend only what it generates.
								</h1>
								<p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
									AgentFramer uses workspace balance, not subscriptions. Add
									funds through Stripe, connect your MCP client, and generation
									costs are deducted as image, video, and audio jobs run.
								</p>
								<div className="mt-7 flex flex-wrap items-center justify-center gap-3">
									<Link
										to="/onboard"
										className="inline-flex items-center gap-2 rounded-md bg-brand-accent px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-accent/90"
									>
										Top up first
										<ArrowRight className="size-4" />
									</Link>
									<Link
										to="/help/mcp-tools"
										className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
									>
										View MCP tools
									</Link>
								</div>
							</Reveal>
						</section>

						<section className="homepage-hover-cell rounded-md bg-background py-8 sm:py-10">
							<PricingTable />
						</section>

						<section className="grid grid-cols-1 gap-px bg-border md:grid-cols-3">
							{valueProps.map((item, index) => {
								const Icon = item.icon;
								return (
									<div
										key={item.title}
										className="homepage-hover-cell rounded-md bg-background p-8"
									>
										<Reveal delay={index * 80}>
											<Icon className="size-5 text-[#6B3FF2]" />
											<h2 className="mt-4 text-xl font-semibold">
												{item.title}
											</h2>
											<p className="mt-2 text-sm leading-relaxed text-muted-foreground">
												{item.body}
											</p>
										</Reveal>
									</div>
								);
							})}
						</section>

						<section className="homepage-hover-cell rounded-md bg-background px-8 py-10 sm:py-12">
							<Reveal className="mx-auto max-w-2xl text-center">
								<h2 className="text-3xl font-semibold">
									Provider usage stays transparent.
								</h2>
								<p className="mt-3 text-base leading-relaxed text-muted-foreground">
									Every top-up and generation charge is visible in workspace
									billing history, so teams can track media spend without
									recurring plans or surprise invoices.
								</p>
							</Reveal>
						</section>
					</div>
				</div>
			</main>
			<PublicFooter />
		</div>
	);
}
